import { Injectable } from '@angular/core';
import { BehaviorSubject, take } from 'rxjs';
import { DateTime } from 'luxon';
import { APIResponse, DbDocument } from 'fakturnia-shared';

import { DocumentsApiService } from './api/documents.api.service';
import { KSeFApiService } from './api/ksef.api.service';
import { DialogService } from './dialog.service';
import { SnackbarService } from './snackbar.service';
import { SynchronizationService } from './synchronization.service';

/**
 * Stan wysyłki do KSeF - emitowany przez sendState$
 */
export interface KsefSendState {
    /** Status wysyłki */
    status: 'idle' | 'sending' | 'success' | 'processing' | 'error';
    /** ID dokumentu (jeśli wysyłka aktywna) */
    documentId?: string;
    /** Numer KSeF (jeśli sukces) */
    ksefNumber?: string;
    /** Komunikat błędu (jeśli error) */
    errorMessage?: string;
}

@Injectable({
    providedIn: 'root'
})
export class KsefActionsService {

    /** Stan wysyłki do KSeF - komponenty mogą subskrybować */
    private _sendState = new BehaviorSubject<KsefSendState>({ status: 'idle' });
    public sendState$ = this._sendState.asObservable();

    constructor(
        private _documentsApiService: DocumentsApiService,
        private _ksefApiService: KSeFApiService,
        private _dialogService: DialogService,
        private _snackbarService: SnackbarService,
        private _synchronizationService: SynchronizationService
    ) { }

    /**
     * Wysyła fakturę do KSeF z dialogiem potwierdzenia.
     * Po wysłaniu listing odświeży się automatycznie przez DataSynchronizerService subscription.
     * @param document Dokument do wysłania
     */
    public sendToKsefWithConfirmation(document: DbDocument): void {
        // Walidacja
        if (!document._id) {
            this._snackbarService.showSnackBar('Brak ID dokumentu', 'error');
            return;
        }

        if (!document.isPaid) {
            this._snackbarService.showSnackBar(
                'Faktura nie została oznaczona jako wystawiona. Przed wysłaniem faktury do KSeF musisz oznaczyć ją jako wystawioną.',
                'warning',
                3000
            );
            return;
        }

        // Sprawdź datę wystawienia
        const issueDate = document.issueDate;
        const today = DateTime.now().toFormat('yyyy-MM-dd');
        
        if (issueDate !== today) {
            this._snackbarService.showSnackBar(
                'Faktura nie została wystawiona w dniu dzisiejszym. Skoryguj datę wystawienia i spróbuj ponownie.',
                'warning',
                3000
            );
            return;
        }

        // Komunikat dla ponownej próby wysłania
        const isRetry = document.ksefSent === false;
        const message = isRetry
            ? 'Czy chcesz ponownie spróbować wysłać tę fakturę do KSeF? Poprzednia próba zakończyła się błędem.'
            : 'Czy na pewno chcesz wysłać tę fakturę do KSeF?\n\nFaktura zostanie wysłana natychmiast.\n\nTa operacja jest nieodwracalna - faktura nie będzie mogła być już edytowana. Można będzie ją tylko skorygować poprzez wystawienie faktury korygującej.';

        this._dialogService.confirmDialog({
            title: isRetry ? 'Ponowne wysłanie faktury do KSeF' : 'Wyślij fakturę do KSeF',
            message: message,
            confirmText: isRetry ? 'Spróbuj ponownie' : 'Wyślij do KSeF',
            cancelText: 'Anuluj',
            type: 'info'
        }).subscribe(confirmed => {
            if (confirmed) {
                this._performKsefSend(document._id!);
            }
        });
    }

    /**
     * Wystawia szkic (oznacza jako wystawiony) i wysyła do KSeF.
     * Używane w wersji webowej, gdy użytkownik chce jednym kliknięciem wystawić i wysłać.
     */
    public issueAndSendToKsefWithConfirmation(document: DbDocument): void {
        if (!document._id) {
            this._snackbarService.showSnackBar('Brak ID dokumentu', 'error');
            return;
        }

        const issueDate = document.issueDate;
        const today = DateTime.now().toFormat('yyyy-MM-dd');

        if (issueDate !== today) {
            this._snackbarService.showSnackBar(
                'Faktura nie została wystawiona w dniu dzisiejszym. Skoryguj datę wystawienia i spróbuj ponownie.',
                'warning',
                3000
            );
            return;
        }

        this._dialogService.confirmDialog({
            title: 'Wystaw i wyślij do KSeF',
            message: 'Czy na pewno chcesz wystawić tę fakturę i wysłać ją do KSeF?\n\nFaktura zostanie oznaczona jako wystawiona i natychmiast wysłana do KSeF.\n\nTa operacja jest nieodwracalna - faktura nie będzie mogła być już edytowana. Można będzie ją tylko skorygować poprzez wystawienie faktury korygującej.',
            confirmText: 'Wystaw i wyślij',
            cancelText: 'Anuluj',
            type: 'info'
        }).subscribe(confirmed => {
            if (confirmed) {
                this._issueAndSend(document._id!);
            }
        });
    }

    /**
     * Oznacza dokument jako wystawiony, a następnie wysyła do KSeF
     */
    private _issueAndSend(documentId: string): void {
        this._sendState.next({ status: 'sending', documentId });

        this._documentsApiService.markDocumentsAsPaidOrTemplate([documentId], true)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this._synchronizationService.synchronize('document-issued');
                    this._performKsefSend(documentId);
                },
                error: (error) => {
                    console.error('Błąd wystawiania dokumentu:', error);
                    this._sendState.next({ status: 'error', documentId, errorMessage: 'Błąd podczas wystawiania dokumentu' });
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas wystawiania dokumentu',
                        'error',
                        5000
                    );
                }
            });
    }

    /**
     * Resetuje stan wysyłki (np. po zamknięciu overlay sukcesu)
     */
    public resetSendState(): void {
        this._sendState.next({ status: 'idle' });
    }

    /**
     * Wykonuje wysłanie do KSeF w trybie instant (bez dialogu potwierdzenia)
     */
    private _performKsefSend(documentId: string): void {
        console.log('📤 Wysyłanie faktury do KSeF (instant)...');

        // Emituj stan: wysyłanie
        this._sendState.next({ status: 'sending', documentId });

        this._ksefApiService.sendInstant(documentId).pipe(take(1)).subscribe({
            next: async (response: APIResponse) => {
                if (response.success) {
                    const status = response.data?.status;

                    if (status === 'processing') {
                        // Faktura wysłana ale jeszcze przetwarzana - cron dokończy
                        console.log('⏳ Faktura wysłana, przetwarzanie trwa...');
                        this._sendState.next({ status: 'processing', documentId });
                        this._snackbarService.showSnackBar(
                            'Faktura została wysłana do KSeF. Przetwarzanie trwa dłużej niż zwykle - status zostanie zaktualizowany automatycznie.',
                            'info',
                            8000
                        );
                    } else {
                        // Sukces - faktura przetworzona z numerem KSeF
                        const ksefNumber = response.data?.ksefNumber;
                        console.log('✅ Faktura wysłana do KSeF:', ksefNumber);
                        this._sendState.next({ status: 'success', documentId, ksefNumber });
                    }

                    // Synchronizuj dane - listing odświeży się automatycznie
                    this._synchronizationService.synchronize();
                } else {
                    console.warn('⚠️ Błąd wysyłki:', response);
                    const errorMsg = response.message || 'Błąd podczas wysyłki faktury do KSeF';
                    this._sendState.next({ status: 'error', documentId, errorMessage: errorMsg });
                    this._snackbarService.showSnackBar(errorMsg, 'error', 5000);
                    this._synchronizationService.synchronize();
                }
            },
            error: (error) => {
                console.error('❌ Błąd wysyłki instant:', error);
                const errorMsg = error.error?.message || 'Wystąpił błąd podczas wysyłki faktury do KSeF';
                this._sendState.next({ status: 'error', documentId, errorMessage: errorMsg });
                this._snackbarService.showSnackBar(errorMsg, 'error', 5000);
                this._synchronizationService.synchronize();
            }
        });
    }
}
